import { createFileRoute } from "@tanstack/react-router";
import * as React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { requireAuth } from "~/lib/auth-guard";
import { orpc } from "~/lib/orpc-client";
import { queryClient } from "~/lib/query-client";
import { Skeleton, useToast } from "@alchema/ui";
import { RouteErrorBoundary } from "~/components/RouteErrorBoundary";
import { EmptyState } from "~/components/shared/EmptyState";
import { AiGeneratedBadge } from "~/components/AiGeneratedBadge";
import * as m from "~/paraglide/messages";

/* ── Types ── */

interface InterviewStory {
  id: string;
  userId: string;
  title: string;
  situation: string;
  task: string;
  action: string;
  result: string;
  reflection: string | null;
  tags: string[];
  jobIds: string[];
  skills: string[];
  archetype: string | null;
  isMaster: boolean;
  createdAt: string;
  updatedAt: string;
}

interface StoryMappingResult {
  requirement: string;
  matchedStory: InterviewStory | null;
  matchStrength: "strong" | "partial" | "none";
  suggestedAngle?: string;
}

/* ── Route ── */

export const Route = createFileRoute("/story-bank")({
  beforeLoad: async () => {
    await requireAuth();
  },
  loader: async () => {
    await Promise.all([
      queryClient.prefetchQuery(orpc.storyBank.list.queryOptions({ input: undefined })),
      queryClient.prefetchQuery(orpc.job.list.queryOptions({ input: undefined })),
      queryClient.prefetchQuery(orpc.resume.list.queryOptions({ input: undefined })),
    ]);
    return null;
  },
  component: StoryBankPage,
  pendingComponent: StoryBankSkeleton,
  errorComponent: RouteErrorBoundary,
});

/* ── Skeleton ── */

/**
 * AI-generate panel header with EU AI Act Article 50 transparency disclosure.
 * The badge sits adjacent to the AI STAR-story generation controls.
 */
export function StoryGenerateHeader() {
  return (
    <>
      <p className="section-label mb-3">{m.story_bank_generate_heading()}</p>
      <p className="mb-2 text-xs" style={{ color: "var(--text-muted)" }}>
        {m.story_bank_generate_description()}
      </p>
      {/* AI transparency badge (EU AI Act Article 50) */}
      <div className="mb-4">
        <AiGeneratedBadge />
      </div>
    </>
  );
}

function StoryBankSkeleton() {
  return (
    <div className="animate-fade-up">
      <div className="page-header">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-8 w-24" />
      </div>
      <div className="mt-6 overflow-hidden rounded-lg border" style={{ borderColor: "var(--border-subtle)" }}>
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex gap-4 px-4 py-3" style={{ borderTop: i > 1 ? "1px solid var(--border-subtle)" : undefined }}>
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-4 w-20" />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Empty form state ── */

const EMPTY_FORM = {
  title: "",
  situation: "",
  task: "",
  action: "",
  result: "",
  reflection: "",
  tags: "",
  skills: "",
};

/* ── Strength badge ── */

const STRENGTH_BADGE: Record<string, { label: string; cls: string }> = {
  strong: { label: m.story_bank_strength_strong(), cls: "badge badge-success" },
  partial: { label: m.story_bank_strength_partial(), cls: "badge badge-warning" },
  none: { label: m.story_bank_strength_gap(), cls: "badge badge-error" },
};

/* ── STAR indicator ── */

function StarIndicator({ story }: { story: InterviewStory }) {
  const components = [
    { key: "S", filled: story.situation.trim().length > 0 },
    { key: "T", filled: story.task.trim().length > 0 },
    { key: "A", filled: story.action.trim().length > 0 },
    { key: "R", filled: story.result.trim().length > 0 },
  ];

  return (
    <span className="inline-flex items-center gap-1">
      {components.map(({ key, filled }) => (
        <span
          key={key}
          title={key}
          className="inline-flex h-4 w-4 items-center justify-center rounded text-[9px] font-bold"
          style={{
            backgroundColor: filled ? "var(--accent-subtle)" : "var(--bg-secondary)",
            color: filled ? "var(--accent-text)" : "var(--text-muted)",
          }}
        >
          {key}
        </span>
      ))}
    </span>
  );
}

/* ── Category dot ── */

function CategoryDot({ archetype }: { archetype: string | null }) {
  if (!archetype) {
    return <span className="text-xs" style={{ color: "var(--text-muted)" }}>—</span>;
  }

  return (
    <span className="inline-flex items-center gap-1.5 text-xs" style={{ color: "var(--text-secondary)" }}>
      <span
        className="inline-block h-1.5 w-1.5 rounded-full flex-shrink-0"
        style={{ backgroundColor: "var(--accent)" }}
        aria-hidden="true"
      />
      {archetype}
    </span>
  );
}

/* ── Inline Add Row ── */

function InlineAddRow({
  onSave,
  isSaving,
}: {
  onSave: (title: string) => void;
  isSaving: boolean;
}) {
  const [title, setTitle] = React.useState("");

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" && title.trim()) {
      onSave(title.trim());
      setTitle("");
    }
    if (e.key === "Escape") {
      setTitle("");
    }
  }

  return (
    <tr style={{ borderTop: "1px solid var(--border-subtle)" }}>
      <td colSpan={6} className="px-4 py-2">
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={`+ ${m.story_bank_new_story()} — press Enter to save`}
          disabled={isSaving}
          className="w-full bg-transparent text-sm outline-none placeholder:text-[var(--text-muted)] focus:placeholder:text-transparent"
          style={{ color: "var(--text)" }}
        />
      </td>
    </tr>
  );
}

/* ── Expanded Story Row ── */

function ExpandedStoryRow({
  story,
  colSpan,
  onEdit,
  onDelete,
}: {
  story: InterviewStory;
  colSpan: number;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <tr style={{ backgroundColor: "var(--surface-inset)" }}>
      <td colSpan={colSpan} className="px-6 py-4">
        <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
          {[
            { label: m.story_bank_label_situation(), text: story.situation },
            { label: m.story_bank_label_task(), text: story.task },
            { label: m.story_bank_label_action(), text: story.action },
            { label: m.story_bank_label_result(), text: story.result },
          ].map(({ label, text }) => (
            <div key={label}>
              <p className="section-label mb-1">{label}</p>
              <p className="text-xs leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                {text}
              </p>
            </div>
          ))}
          {story.reflection && (
            <div className="sm:col-span-2">
              <p className="section-label mb-1">{m.story_bank_label_reflection()}</p>
              <p className="text-xs leading-relaxed italic" style={{ color: "var(--text-muted)" }}>
                {story.reflection}
              </p>
            </div>
          )}
          {story.skills.length > 0 && (
            <div>
              <p className="section-label mb-1">{m.story_bank_label_skills()}</p>
              <div className="flex flex-wrap gap-1">
                {story.skills.map((s) => (
                  <span
                    key={s}
                    className="rounded px-1.5 py-0.5 text-[10px]"
                    style={{ backgroundColor: "var(--bg-secondary)", color: "var(--text-secondary)" }}
                  >
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}
          {story.tags.length > 0 && (
            <div>
              <p className="section-label mb-1">{m.story_bank_label_tags()}</p>
              <div className="flex flex-wrap gap-1">
                {story.tags.map((t) => (
                  <span
                    key={t}
                    className="rounded px-1.5 py-0.5 text-[10px]"
                    style={{ backgroundColor: "var(--bg-secondary)", color: "var(--text-muted)" }}
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="mt-4 flex gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            className="btn btn-secondary btn-sm"
          >
            {m.story_bank_edit_btn()}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="btn btn-ghost btn-sm"
            style={{ color: "var(--error)" }}
            data-testid="story-delete-btn"
          >
            {m.story_bank_delete_btn()}
          </button>
        </div>
      </td>
    </tr>
  );
}

/* ── Story Table ── */

function StoryTable({
  stories,
  onEdit,
  onDeleteRequest,
  onInlineCreate,
  isCreating,
}: {
  stories: InterviewStory[];
  onEdit: (story: InterviewStory) => void;
  onDeleteRequest: (id: string) => void;
  onInlineCreate: (title: string) => void;
  isCreating: boolean;
}) {
  const [expandedId, setExpandedId] = React.useState<string | null>(null);

  function toggleExpand(id: string) {
    setExpandedId((prev) => (prev === id ? null : id));
  }

  return (
    <div
      className="overflow-hidden rounded-lg border"
      style={{ borderColor: "var(--border-subtle)" }}
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="story-bank-list">
          <thead>
            <tr style={{ backgroundColor: "var(--surface-raised)", borderBottom: "1px solid var(--border-subtle)" }}>
              <th className="section-label px-4 py-2.5 text-left">{m.story_bank_label_title()}</th>
              <th className="section-label px-4 py-2.5 text-left">Category</th>
              <th className="section-label px-4 py-2.5 text-left">STAR</th>
              <th className="section-label px-4 py-2.5 text-left">Created</th>
              <th className="section-label px-4 py-2.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {stories.map((story, idx) => {
              const isExpanded = expandedId === story.id;
              return (
                <React.Fragment key={story.id}>
                  <tr
                    className="cursor-pointer transition-colors duration-100 hover:bg-[var(--bg-secondary)]"
                    style={{ borderTop: idx > 0 ? "1px solid var(--border-subtle)" : undefined }}
                    onClick={() => toggleExpand(story.id)}
                    data-testid="story-card"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span
                          className="text-sm font-medium"
                          style={{ color: "var(--text)" }}
                        >
                          {story.title}
                        </span>
                        {story.isMaster && (
                          <span className="badge badge-accent text-[10px] shrink-0">
                            {m.story_bank_master_badge()}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <CategoryDot archetype={story.archetype} />
                    </td>
                    <td className="px-4 py-3">
                      <StarIndicator story={story} />
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs tabular-nums" style={{ color: "var(--text-muted)" }}>
                        {new Date(story.createdAt).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleExpand(story.id); }}
                        className="text-xs transition-colors duration-150"
                        style={{ color: isExpanded ? "var(--accent)" : "var(--text-muted)" }}
                        aria-expanded={isExpanded}
                      >
                        {isExpanded ? "Collapse" : "Expand"}
                      </button>
                    </td>
                  </tr>
                  {isExpanded && (
                    <ExpandedStoryRow
                      story={story}
                      colSpan={5}
                      onEdit={() => onEdit(story)}
                      onDelete={() => onDeleteRequest(story.id)}
                    />
                  )}
                </React.Fragment>
              );
            })}

            {/* Inline add row */}
            <InlineAddRow onSave={onInlineCreate} isSaving={isCreating} />
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Book icon for empty state ── */

function BookIcon() {
  return (
    <svg
      aria-hidden="true"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"
      />
    </svg>
  );
}

/* ── Main Page ── */

function StoryBankPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // UI state
  const [showForm, setShowForm] = React.useState(false);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [form, setForm] = React.useState(EMPTY_FORM);
  const [showGenerate, setShowGenerate] = React.useState(false);
  const [generateJobId, setGenerateJobId] = React.useState("");
  const [generateResumeId, setGenerateResumeId] = React.useState("");
  const [showMapping, setShowMapping] = React.useState(false);
  const [mapJobId, setMapJobId] = React.useState("");
  const [mappingResults, setMappingResults] = React.useState<StoryMappingResult[] | null>(null);
  const [deleteTarget, setDeleteTarget] = React.useState<string | null>(null);
  const [filterText, setFilterText] = React.useState("");

  // Queries
  const storiesQuery = useQuery(orpc.storyBank.list.queryOptions({ input: undefined }));
  const jobsQuery = useQuery(orpc.job.list.queryOptions({ input: undefined }));
  const resumesQuery = useQuery(orpc.resume.list.queryOptions({ input: undefined }));

  const stories: InterviewStory[] = (storiesQuery.data as InterviewStory[] | undefined) ?? [];
  const jobs = jobsQuery.data ?? [];
  const resumes = resumesQuery.data ?? [];

  // Mutations
  const createMutation = useMutation(
    orpc.storyBank.create.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.storyBank.list.key({}) });
        resetForm();
        toast(m.story_bank_toast_created(), "success");
      },
      onError: (err: Error) => toast(err.message || m.story_bank_toast_create_error(), "error"),
    })
  );

  const updateMutation = useMutation(
    orpc.storyBank.update.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.storyBank.list.key({}) });
        resetForm();
        toast(m.story_bank_toast_updated(), "success");
      },
      onError: (err: Error) => toast(err.message || m.story_bank_toast_update_error(), "error"),
    })
  );

  const deleteMutation = useMutation(
    orpc.storyBank.delete.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.storyBank.list.key({}) });
        toast(m.story_bank_toast_deleted(), "success");
      },
      onError: (err: Error) => toast(err.message || m.story_bank_toast_delete_error(), "error"),
    })
  );

  const generateMutation = useMutation(
    orpc.storyBank.generate.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.storyBank.list.key({}) });
        setShowGenerate(false);
        setGenerateJobId("");
        setGenerateResumeId("");
        toast(m.story_bank_toast_generated(), "success");
      },
      onError: (err: Error) => toast(err.message || m.story_bank_toast_generate_error(), "error"),
    })
  );

  const mapMutation = useMutation(
    orpc.storyBank.mapToJob.mutationOptions({
      onSuccess: (data) => {
        setMappingResults(data.mappings.map((mp) => ({
          requirement: mp.requirement,
          matchedStory: mp.bestStoryIndex != null ? (stories[mp.bestStoryIndex] ?? null) : null,
          matchStrength: mp.matchStrength,
        })));
      },
      onError: (err: Error) => toast(err.message || m.story_bank_toast_map_error(), "error"),
    })
  );

  // Helpers
  function resetForm() {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(false);
  }

  function openEdit(story: InterviewStory) {
    setForm({
      title: story.title,
      situation: story.situation,
      task: story.task,
      action: story.action,
      result: story.result,
      reflection: story.reflection ?? "",
      tags: story.tags.join(", "),
      skills: story.skills.join(", "),
    });
    setEditingId(story.id);
    setShowForm(true);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload = {
      title: form.title,
      situation: form.situation,
      task: form.task,
      action: form.action,
      result: form.result,
      reflection: form.reflection || null,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
    };

    if (editingId) {
      updateMutation.mutate({ id: editingId, ...payload });
    } else {
      createMutation.mutate(payload);
    }
  }

  function handleInlineCreate(title: string) {
    createMutation.mutate({
      title,
      situation: "",
      task: "",
      action: "",
      result: "",
      reflection: null,
      tags: [],
      skills: [],
    });
  }

  // Filter
  const filtered = React.useMemo(() => {
    if (!filterText.trim()) return stories;
    const q = filterText.toLowerCase();
    return stories.filter(
      (s) =>
        s.title.toLowerCase().includes(q) ||
        s.tags.some((t) => t.toLowerCase().includes(q)) ||
        s.skills.some((sk) => sk.toLowerCase().includes(q))
    );
  }, [stories, filterText]);

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="animate-fade-up">
      {/* ── Page header ── */}
      <div className="page-header">
        <div>
          <h1
            className="font-semibold tracking-tight"
            style={{
              fontSize: "1.375rem",
              fontFamily: "var(--font-display)",
              color: "var(--text)",
            }}
          >
            {m.story_bank_page_title()}
          </h1>
          <p className="mt-0.5 text-sm" style={{ color: "var(--text-muted)" }}>
            {stories.length} {stories.length === 1 ? "story" : "stories"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowMapping(!showMapping)}
            className="btn btn-secondary btn-sm"
          >
            {showMapping ? m.story_bank_close_mapping() : m.story_bank_map_to_job()}
          </button>
          <button
            onClick={() => setShowGenerate(!showGenerate)}
            className="btn btn-secondary btn-sm"
          >
            {showGenerate ? m.story_bank_close() : m.story_bank_ai_generate()}
          </button>
          <button
            onClick={() => {
              if (showForm && !editingId) {
                resetForm();
              } else {
                setForm(EMPTY_FORM);
                setEditingId(null);
                setShowForm(true);
              }
            }}
            className="btn btn-primary btn-sm"
          >
            {showForm && !editingId ? m.story_bank_cancel() : `+ ${m.story_bank_new_story()}`}
          </button>
        </div>
      </div>

      {/* ── AI Generate panel ── */}
      {showGenerate && (
        <div
          className="mb-6 rounded-lg border p-5 animate-fade-up"
          style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface)" }}
        >
          <StoryGenerateHeader />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="gen-job-select" className="block text-xs font-medium mb-1" style={{ color: "var(--text-secondary)" }}>
                {m.story_bank_label_job()}
              </label>
              <select
                id="gen-job-select"
                value={generateJobId}
                onChange={(e) => setGenerateJobId(e.target.value)}
                className="input w-full"
              >
                <option value="">{m.story_bank_select_job_placeholder()}</option>
                {jobs.map((j: { id: string; title?: string; company?: string }) => (
                  <option key={j.id} value={j.id}>
                    {j.title ?? "Untitled"} {j.company ? `at ${j.company}` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="gen-resume-select" className="block text-xs font-medium mb-1" style={{ color: "var(--text-secondary)" }}>
                {m.story_bank_label_resume()}
              </label>
              <select
                id="gen-resume-select"
                value={generateResumeId}
                onChange={(e) => setGenerateResumeId(e.target.value)}
                className="input w-full"
              >
                <option value="">{m.story_bank_select_resume_placeholder()}</option>
                {resumes.map((r: { id: string; title?: string; name?: string }) => (
                  <option key={r.id} value={r.id}>
                    {r.title ?? r.name ?? "Untitled"}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button
              onClick={() => {
                if (!generateJobId || !generateResumeId) {
                  toast(m.story_bank_select_job_and_resume(), "error");
                  return;
                }
                generateMutation.mutate({ resumeId: generateResumeId, jobId: generateJobId });
              }}
              disabled={generateMutation.isPending}
              className="btn btn-primary btn-sm"
            >
              {generateMutation.isPending ? m.story_bank_generating() : m.story_bank_generate_btn()}
            </button>
            <button onClick={() => setShowGenerate(false)} className="btn btn-ghost btn-sm">
              {m.story_bank_cancel()}
            </button>
          </div>
        </div>
      )}

      {/* ── Map to Job panel ── */}
      {showMapping && (
        <div
          className="mb-6 rounded-lg border p-5 animate-fade-up"
          style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface)" }}
        >
          <p className="section-label mb-3">{m.story_bank_mapping_heading()}</p>
          <p className="mb-4 text-xs" style={{ color: "var(--text-muted)" }}>
            {m.story_bank_mapping_description()}
          </p>
          <div className="max-w-sm">
            <label htmlFor="map-job-select" className="block text-xs font-medium mb-1" style={{ color: "var(--text-secondary)" }}>
              {m.story_bank_label_job()}
            </label>
            <select
              id="map-job-select"
              value={mapJobId}
              onChange={(e) => setMapJobId(e.target.value)}
              className="input w-full"
            >
              <option value="">{m.story_bank_select_job_placeholder()}</option>
              {jobs.map((j: { id: string; title?: string; company?: string }) => (
                <option key={j.id} value={j.id}>
                  {j.title ?? "Untitled"} {j.company ? `at ${j.company}` : ""}
                </option>
              ))}
            </select>
          </div>
          <div className="mt-4 flex gap-2">
            <button
              onClick={() => {
                if (!mapJobId) {
                  toast(m.story_bank_select_job_first(), "error");
                  return;
                }
                mapMutation.mutate({ jobId: mapJobId });
              }}
              disabled={mapMutation.isPending}
              className="btn btn-primary btn-sm"
            >
              {mapMutation.isPending ? m.story_bank_analyzing() : m.story_bank_map_stories_btn()}
            </button>
            <button
              onClick={() => { setShowMapping(false); setMappingResults(null); }}
              className="btn btn-ghost btn-sm"
            >
              {m.story_bank_cancel()}
            </button>
          </div>

          {/* Mapping results */}
          {mappingResults && (
            <div className="mt-5 space-y-2">
              <p className="section-label">{m.story_bank_mapping_results_heading()}</p>
              {mappingResults.map((mr, idx) => {
                const badge = STRENGTH_BADGE[mr.matchStrength];
                return (
                  <div
                    key={idx}
                    className="flex items-start justify-between gap-3 rounded-md px-3 py-2.5"
                    style={{ border: "1px solid var(--border-subtle)", backgroundColor: "var(--bg-secondary)" }}
                  >
                    <div className="min-w-0 flex-1">
                      <span className="text-xs font-medium" style={{ color: "var(--text)" }}>
                        {mr.requirement}
                      </span>
                      {mr.matchedStory && (
                        <p className="mt-0.5 text-xs" style={{ color: "var(--text-muted)" }}>
                          {m.story_bank_matched()}: {mr.matchedStory.title}
                        </p>
                      )}
                      {mr.suggestedAngle && (
                        <p className="mt-0.5 text-xs italic" style={{ color: "var(--text-muted)" }}>
                          {m.story_bank_angle()}: {mr.suggestedAngle}
                        </p>
                      )}
                    </div>
                    <span className={`${badge.cls} shrink-0`}>{badge.label}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Create / Edit form ── */}
      {showForm && (
        <div
          className="mb-6 rounded-lg border animate-fade-up"
          style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface)" }}
        >
          <form onSubmit={handleSubmit} className="p-5">
            <p className="section-label mb-4">
              {editingId ? m.story_bank_edit_story() : m.story_bank_create_story()}
            </p>

            <div className="space-y-4">
              {/* Title */}
              <div>
                <label htmlFor="story-title" className="block text-xs font-medium mb-1" style={{ color: "var(--text-secondary)" }}>
                  {m.story_bank_label_title()}
                </label>
                <input
                  id="story-title"
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="input w-full"
                  placeholder={m.story_bank_placeholder_title()}
                  required
                />
              </div>

              {/* STAR+R fields */}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {[
                  { id: "story-situation", key: "situation" as const, label: m.story_bank_label_situation(), placeholder: m.story_bank_placeholder_situation() },
                  { id: "story-task", key: "task" as const, label: m.story_bank_label_task(), placeholder: m.story_bank_placeholder_task() },
                  { id: "story-action", key: "action" as const, label: m.story_bank_label_action(), placeholder: m.story_bank_placeholder_action() },
                  { id: "story-result", key: "result" as const, label: m.story_bank_label_result(), placeholder: m.story_bank_placeholder_result() },
                ].map(({ id, key, label, placeholder }) => (
                  <div key={id}>
                    <label htmlFor={id} className="block text-xs font-medium mb-1" style={{ color: "var(--text-secondary)" }}>
                      {label}
                    </label>
                    <textarea
                      id={id}
                      value={form[key]}
                      onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                      className="input w-full"
                      rows={3}
                      placeholder={placeholder}
                      required
                    />
                  </div>
                ))}
              </div>

              {/* Reflection */}
              <div>
                <label htmlFor="story-reflection" className="block text-xs font-medium mb-1" style={{ color: "var(--text-secondary)" }}>
                  {m.story_bank_label_reflection()}
                </label>
                <textarea
                  id="story-reflection"
                  value={form.reflection}
                  onChange={(e) => setForm({ ...form, reflection: e.target.value })}
                  className="input w-full"
                  rows={2}
                  placeholder={m.story_bank_placeholder_reflection()}
                />
              </div>

              {/* Tags & Skills */}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label htmlFor="story-tags" className="block text-xs font-medium mb-1" style={{ color: "var(--text-secondary)" }}>
                    {m.story_bank_label_tags()}
                  </label>
                  <input
                    id="story-tags"
                    type="text"
                    value={form.tags}
                    onChange={(e) => setForm({ ...form, tags: e.target.value })}
                    className="input w-full"
                    placeholder={m.story_bank_placeholder_tags()}
                  />
                </div>
                <div>
                  <label htmlFor="story-skills" className="block text-xs font-medium mb-1" style={{ color: "var(--text-secondary)" }}>
                    {m.story_bank_label_skills()}
                  </label>
                  <input
                    id="story-skills"
                    type="text"
                    value={form.skills}
                    onChange={(e) => setForm({ ...form, skills: e.target.value })}
                    className="input w-full"
                    placeholder={m.story_bank_placeholder_skills()}
                  />
                </div>
              </div>
            </div>

            <div className="mt-5 flex gap-2">
              <button type="submit" disabled={isSaving} className="btn btn-primary btn-sm">
                {isSaving
                  ? m.story_bank_saving()
                  : editingId
                    ? m.story_bank_update_story()
                    : m.story_bank_create_story()}
              </button>
              <button type="button" onClick={resetForm} className="btn btn-ghost btn-sm">
                {m.story_bank_cancel()}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Filter / search ── */}
      {stories.length > 0 && (
        <div className="mb-4">
          <input
            type="text"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className="input w-full max-w-xs text-sm"
            placeholder={m.story_bank_filter_placeholder()}
          />
        </div>
      )}

      {/* ── Story table ── */}
      {storiesQuery.isLoading ? (
        <StoryBankSkeleton />
      ) : stories.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div style={{ color: "var(--text-muted)", opacity: 0.5 }} className="mb-4">
            <BookIcon />
          </div>
          <p className="text-sm font-medium" style={{ color: "var(--text-secondary)" }}>{m.story_bank_empty_title()}</p>
          <p className="mt-1 text-xs max-w-xs" style={{ color: "var(--text-muted)" }}>{m.story_bank_empty_description()}</p>
          <div className="flex gap-2 mt-5">
            <button
              onClick={() => { setForm(EMPTY_FORM); setEditingId(null); setShowForm(true); }}
              className="btn btn-primary btn-sm"
            >
              {m.story_bank_create_story()}
            </button>
            <button onClick={() => setShowGenerate(true)} className="btn btn-secondary btn-sm">
              {m.story_bank_ai_generate()}
            </button>
          </div>
        </div>
      ) : (
        <StoryTable
          stories={filtered}
          onEdit={openEdit}
          onDeleteRequest={setDeleteTarget}
          onInlineCreate={handleInlineCreate}
          isCreating={createMutation.isPending}
        />
      )}

      {/* Filtered empty state */}
      {stories.length > 0 && filtered.length === 0 && filterText.trim() && (
        <div className="mt-6 text-center">
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>
            {m.story_bank_no_filter_results()}
          </p>
        </div>
      )}

      {/* ── Delete confirmation dialog ── */}
      {deleteTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: "rgba(0,0,0,0.5)" }}
          role="presentation"
          onClick={() => setDeleteTarget(null)}
          onKeyDown={(e) => { if (e.key === "Escape") setDeleteTarget(null); }}
        >
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- dialog container needs event stop-propagation */}
          <div
            className="rounded-xl p-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-dialog-title"
            aria-describedby="delete-dialog-desc"
            ref={(el) => el?.focus()}
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            style={{
              background: "var(--surface)",
              border: "1px solid var(--border)",
              outline: "none",
            }}
          >
            <h3 id="delete-dialog-title" className="text-sm font-semibold" style={{ color: "var(--text)" }}>
              {m.story_bank_delete_confirm_title()}
            </h3>
            <p id="delete-dialog-desc" className="mt-2 text-xs" style={{ color: "var(--text-muted)" }}>
              {m.story_bank_delete_confirm_description()}
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setDeleteTarget(null)} className="btn btn-ghost btn-sm">
                {m.story_bank_cancel()}
              </button>
              <button
                onClick={() => {
                  if (deleteTarget) deleteMutation.mutate({ id: deleteTarget });
                  setDeleteTarget(null);
                }}
                className="btn btn-sm text-white"
                style={{ background: "var(--error)" }}
              >
                {m.story_bank_delete_btn()}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
